import {
  Chip,
  CircularProgress,
  DialogActions,
  IconButton,
  InputAdornment,
  makeStyles,
} from '@material-ui/core';
import { Button as MaterialButton, Paper } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import FilterListIcon from '@material-ui/icons/FilterList';
import SearchIcon from '@material-ui/icons/Search';
import SpeakerNotesOffIcon from '@material-ui/icons/SpeakerNotesOff';
import cn from 'classnames';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { FilterType } from '../api/personal-notes';
import { ClearFilters } from '../components/Pages/Listing/ClearFilters';
import {
  BeforeUnload,
  Button,
  ModalV2,
  Select,
  TextField,
} from '../components/common';
import BeforeChange from '../components/common/before-change';
import BaseLayout from '../components/layout/base-layout';
import NoteEditor from '../components/notes/note-editor';
import NotesList from '../components/notes/notes-list';
import {
  PersonalNotesProvider,
  usePersonalNotes,
} from '../contexts/personal-notes-context';
import { UserContext } from '../contexts/user-context';
import { ProtectedRouteProps } from '../router/type';
import { COLORS } from '../theme/variables';
import { isMobile } from '../utils/functions';
import { CLASS_TRACKING } from '../utils/tracking_class';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: '100%',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',

    [theme.breakpoints.down('xs')]: {
      margin: 0,
      borderRadius: 0,
    },

    '& * ::-webkit-scrollbar': {
      width: 12,
    },

    '& * ::-webkit-scrollbar-thumb': {
      backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_30,

      '&:hover': {
        backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_20,
      },
    },

    '& * ::-webkit-scrollbar-track': {
      backgroundColor: COLORS.COLOR_GRAY_LIGHTENED_45,
    },
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '340px 1fr',
    gridTemplateRows: '1fr',
    gridAutoRows: 'min-content',
    alignItems: 'stretch',
    alignContent: 'start',
    flex: 1,
    minHeight: 0,
    maxHeight: '100%',
    overflow: 'hidden',
    borderTop: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
  },
  noteBlock: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    maxHeight: '100%',
    overflow: 'hidden',
    alignSelf: 'stretch',
    width: '100%',
    boxSizing: 'border-box',

    [theme.breakpoints.down('xs')]: {
      position: 'relative',
    },
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    minHeight: 0,
  },
  nonSelectedIcon: {
    fontSize: 80,
    color: COLORS.COLOR_GRAY_LIGHTENED_30,
  },
}));

const useHeaderStyles = makeStyles((theme) => ({
  cardTopSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    gap: 10,
    flexShrink: 0,
    width: '100%',
    boxSizing: 'border-box',

    '& > button': {
      minWidth: 'min-content',
    },
  },
  cardTopSectionMobile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: '12px 16px',
    gap: 10,
    flexShrink: 0,
    width: '100%',
    boxSizing: 'border-box',
    paddingLeft: 'max(16px, env(safe-area-inset-left, 16px))',
    paddingRight: 'max(16px, env(safe-area-inset-right, 16px))',
    paddingTop: 'max(12px, env(safe-area-inset-top, 12px))',
    paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))',
  },
  addNoteBtn: {
    padding: '6.5px 16px',
  },
  filtersContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flex: 1,

    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  searchAndButtonRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',

    '& > div:first-child': {
      flexGrow: 1,
    },
  },
  typeSelectRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    width: '100%',
  },
  filterChip: {
    backgroundColor: COLORS.COLOR_BLUE_BASE,
    '& .MuiChip-label': {
      color: COLORS.COLOR_ADDITIONAL_WHITE,
    },
    '& .MuiChip-deleteIcon': {
      color: COLORS.COLOR_ADDITIONAL_WHITE,
    },
  },
  searchInput: {
    width: 200,
    '& .MuiOutlinedInput-input': {
      paddingRight: '12px',
    },

    '@media (max-width: 800px)': {
      width: 180,
    },
  },
  searchInputMobile: {
    flex: 1,
    width: '100%',
    minWidth: 0,
    '& .MuiOutlinedInput-input': {
      paddingRight: '12px',
    },
    '& .MuiInputAdornment-positionEnd': {
      marginLeft: 0,
    },
  },
  typeSelect: {
    width: 200,
    '& > div': {
      height: 38,
      minHeight: 38,
    },

    '@media (max-width: 800px)': {
      width: 180,
    },
  },
  typeSelectMobile: {
    width: '100%',
    flex: 1,
    '& > div': {
      height: 38,
      minHeight: 38,
    },
  },
  clearFiltersDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  filtersModalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  modalActions: {
    padding: '32px 0 0',
  },
  iconButton: {
    padding: 7,
    borderRadius: '4px',
  },
  filterIconButton: {
    padding: 6,
    color: COLORS.COLOR_BLUE_BASE,
    borderColor: COLORS.COLOR_BLUE_BASE,
    borderWidth: 1,
    borderStyle: 'solid',

    '&:disabled': {
      borderColor: 'rgba(0, 0, 0, 0.26)',
    },

    '&:hover': {
      color: COLORS.COLOR_BLUE_LIGHTENED_5,
      background: 'transparent',
      borderColor: COLORS.COLOR_BLUE_LIGHTENED_5,
    },
  },
  addNoteIconButton: {
    background: COLORS.COLOR_BLUE_BASE,
    color: 'white',

    '&:hover': {
      color: 'white',
      background: COLORS.COLOR_BLUE_LIGHTENED_5,
    },
  },
}));

function FilterIconButton() {
  const classes = useHeaderStyles();
  const { loading, typeFilter, setTypeFilter, typeFilterOptions } =
    usePersonalNotes();

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [localTypeFilter, setLocalTypeFilter] = useState<
    FilterType | undefined
  >(typeFilter);

  const handleOpen = useCallback(() => {
    setLocalTypeFilter(typeFilter);
    setFiltersOpen(true);
  }, [typeFilter]);

  const handleClose = useCallback(() => {
    setFiltersOpen(false);
  }, []);

  const handleApply = useCallback(() => {
    setTypeFilter(localTypeFilter);
    handleClose();
  }, [localTypeFilter, setTypeFilter, handleClose]);

  const handleClearFilters = useCallback(() => {
    setLocalTypeFilter(undefined);
  }, []);

  return (
    <>
      <IconButton
        className={cn(classes.iconButton, classes.filterIconButton)}
        data-testid='button-filter-personal-notes'
        onClick={handleOpen}
        disabled={loading}>
        <FilterListIcon />
      </IconButton>
      <ModalV2
        width='480px'
        open={filtersOpen}
        onClose={handleClose}
        titleProps={{ title: 'Filters' }}>
        <div className={classes.filtersModalContent}>
          <Select<FilterType | undefined>
            className={cn(
              classes.typeSelectMobile,
              CLASS_TRACKING.FILTER_ELEMENT,
            )}
            value={localTypeFilter}
            options={typeFilterOptions}
            onSelect={(option) => setLocalTypeFilter(option?.value)}
            isActive={!!localTypeFilter}
            placeholder='All Types'
            menuPortalTarget={
              typeof document !== 'undefined' ? document.body : null
            }
          />
        </div>
        <DialogActions className={classes.modalActions}>
          <Button
            variant='outlined'
            onClick={handleClearFilters}
            disabled={!localTypeFilter}
            data-testid='button-clear-filters'>
            Clear filters
          </Button>
          <Button
            variant='outlined'
            onClick={handleClose}
            data-testid='button-cancel-filters'>
            Cancel
          </Button>
          <Button onClick={handleApply} data-testid='button-apply-filters'>
            Apply
          </Button>
        </DialogActions>
      </ModalV2>
    </>
  );
}

function PersonalNotesHeaderMobile() {
  const classes = useHeaderStyles();
  const { hasAccessToAction } = useContext(UserContext);
  const {
    searchValue,
    handleSearch,
    handleAddNewNote,
    loading,
    setSearchValue,
    setSearchFilter,
    typeFilter,
    setTypeFilter,
    typeFilterOptions,
    searchFilter,
  } = usePersonalNotes();

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    setSearchFilter(undefined);
  }, [setSearchValue, setSearchFilter]);

  const handleRemoveTypeFilter = useCallback(() => {
    setTypeFilter(undefined);
  }, [setTypeFilter]);

  const hasActiveFilters = !!typeFilter || !!searchFilter;

  const typeFilterLabel = typeFilterOptions.find(
    (option) => option.value === typeFilter,
  )?.label;

  return (
    <div className={classes.cardTopSectionMobile}>
      <div className={classes.searchAndButtonRow}>
        <TextField
          className={cn(
            classes.searchInputMobile,
            CLASS_TRACKING.FILTER_ELEMENT,
          )}
          value={searchValue}
          onChange={handleSearch}
          placeholder='Search'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchValue ? (
              <InputAdornment position='end'>
                <IconButton
                  size='small'
                  onClick={handleClearSearch}
                  data-testid='button-clear-search'>
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          small
        />
        <FilterIconButton />
        {hasAccessToAction('personalNotes.create') ? (
          <IconButton
            className={cn(classes.iconButton, classes.addNoteIconButton)}
            data-testid='button-new-personal-note'
            onClick={handleAddNewNote}
            disabled={loading}>
            <AddIcon />
          </IconButton>
        ) : null}
      </div>
      {hasActiveFilters && (
        <div className={classes.filtersRow}>
          {typeFilter && typeFilterLabel && (
            <Chip
              label={typeFilterLabel}
              onDelete={handleRemoveTypeFilter}
              className={classes.filterChip}
            />
          )}
        </div>
      )}
    </div>
  );
}

function PersonalNotesHeaderDesktop() {
  const classes = useHeaderStyles();
  const { hasAccessToAction } = useContext(UserContext);
  const {
    searchValue,
    typeFilter,
    setTypeFilter,
    typeFilterOptions,
    searchFilter,
    resetFilters,
    handleSearch,
    handleAddNewNote,
    loading,
  } = usePersonalNotes();

  return (
    <div
      className={
        isMobile() ? classes.cardTopSectionMobile : classes.cardTopSection
      }>
      {isMobile() ? (
        <>
          <div className={classes.searchAndButtonRow}>
            <TextField
              className={cn(
                classes.searchInputMobile,
                CLASS_TRACKING.FILTER_ELEMENT,
              )}
              value={searchValue}
              onChange={handleSearch}
              placeholder='Search'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              small
            />
            <FilterIconButton />
            {hasAccessToAction('personalNotes.create') ? (
              <IconButton
                className={cn(classes.iconButton, classes.addNoteIconButton)}
                data-testid='button-new-personal-note'
                onClick={handleAddNewNote}
                disabled={loading}>
                <AddIcon />
              </IconButton>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <div className={classes.filtersContainer}>
            <TextField
              className={cn(classes.searchInput, CLASS_TRACKING.FILTER_ELEMENT)}
              value={searchValue}
              onChange={handleSearch}
              placeholder='Search'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              small
            />
            <Select<FilterType | undefined>
              className={cn(classes.typeSelect, CLASS_TRACKING.FILTER_ELEMENT)}
              value={typeFilter}
              options={typeFilterOptions}
              onSelect={(option) => setTypeFilter(option?.value)}
              isActive={!!typeFilter}
              placeholder='All Types'
              menuPortalTarget={
                typeof document !== 'undefined' ? document.body : null
              }
            />
            <ClearFilters
              onClick={resetFilters}
              className={
                !searchFilter && !typeFilter ? classes.clearFiltersDisabled : ''
              }
            />
          </div>
          {hasAccessToAction('personalNotes.create') ? (
            <MaterialButton
              className={cn(CLASS_TRACKING.ENTITY_ACTION, classes.addNoteBtn)}
              data-testid='button-new-personal-note'
              startIcon={<AddIcon />}
              variant='contained'
              onClick={handleAddNewNote}
              disabled={loading}
              color='primary'>
              Note
            </MaterialButton>
          ) : null}
        </>
      )}
    </div>
  );
}

function PersonalNotesPage({ user }: ProtectedRouteProps) {
  const classes = useStyles();
  const {
    selectedNote,
    loading,
    saving,
    deleting,
    isDirty,
    beforeUnloadState,
    setBeforeUnloadState,
    setSelectedNote,
    addNewNote,
    handleSaveNote,
  } = usePersonalNotes();

  const confirmButtonRenderer = useCallback(
    ({ onConfirm }: { onConfirm: () => any }) => (
      <MaterialButton
        variant='outlined'
        onClick={async () => {
          await handleSaveNote();
          onConfirm();
        }}
        disabled={saving}>
        {loading ? (
          <CircularProgress size={24} color='inherit' />
        ) : (
          'Save the changes'
        )}
      </MaterialButton>
    ),
    [handleSaveNote, loading, saving],
  );

  useEffect(() => {
    if (isMobile()) {
      const body = document.querySelector('body');
      const baseLayout = document.getElementById('base-layout');

      if (body) {
        body.style.height = '100dvh';
      }

      if (baseLayout) {
        baseLayout.style.height = '100dvh';
      }
    }
  }, []);

  return (
    <BaseLayout user={user} hiddenFooter={isMobile()} fullHeight>
      <Paper data-testid='notes-paper' className={classes.paper}>
        {isMobile() ? (
          selectedNote ? null : (
            <PersonalNotesHeaderMobile />
          )
        ) : (
          <PersonalNotesHeaderDesktop />
        )}
        <BeforeUnload
          when={isDirty && !(saving || deleting)}
          title={'Leave the page'}
          body={
            'You are about to leave the page, all unsaved changes will be lost. Do you want to continue?'
          }
          disabled={saving || deleting}
          confirmButtonRenderer={confirmButtonRenderer}
          withPrevPath
        />
        <BeforeChange
          when={isDirty && !(saving || deleting)}
          title={'Leave the note'}
          body={
            'You are about to leave the note, all unsaved changes will be lost. Do you want to continue?'
          }
          disabled={saving || deleting}
          confirmButtonRenderer={confirmButtonRenderer}
          active={!!beforeUnloadState}
          change={() => {
            if (beforeUnloadState?.action === 'add') {
              setBeforeUnloadState(null);
              addNewNote();
            } else {
              setSelectedNote(beforeUnloadState?.note || null);
              setBeforeUnloadState(null);
            }
          }}
          cancel={() => {
            setBeforeUnloadState(null);
          }}
        />
        {isMobile() ? (
          <>
            <NotesList hidden={!!selectedNote} />
            {!!selectedNote && (
              <div className={classes.noteBlock}>
                <NoteEditor />
              </div>
            )}
          </>
        ) : (
          <div className={classes.container}>
            <NotesList />
            <div className={classes.noteBlock}>
              {selectedNote ? (
                <NoteEditor />
              ) : (
                <div className={classes.emptyState}>
                  <SpeakerNotesOffIcon className={classes.nonSelectedIcon} />
                </div>
              )}
            </div>
          </div>
        )}
      </Paper>
    </BaseLayout>
  );
}

function withPersonalNotesProvider(Component: FC<ProtectedRouteProps>) {
  return (props: ProtectedRouteProps) => (
    <PersonalNotesProvider user={props.user}>
      <Component {...props} />
    </PersonalNotesProvider>
  );
}

export default withPersonalNotesProvider(PersonalNotesPage);
